/**
 * ★★ WO lote 3b — O PLACAR DO DIA, puro. Nenhum Firestore, nenhum relógio implícito.
 *
 * É o cartão que fica no topo de Próximos Contatos — a tela que a SDR deixa aberta o dia
 * inteiro. Responde três perguntas, nesta ordem de importância:
 *
 *   1. ★ **esforço**: o que ela controla (leads tentados / follow-ups), contra a meta do dia;
 *   2. ★ **ritmo**: está adiantada ou atrás, dada a hora;
 *   3. ★ **pendências**: o que falta resolver, como LINK para a tela que resolve.
 *
 * ★★ O RESULTADO (agendou, fechou) vem DEPOIS do esforço, e isso é desenho, não acaso: o
 * número grande é o que a pessoa controla hoje. Resultado ela não controla sozinha.
 *
 * ⚠⚠ TRÊS ARMADILHAS, e as três têm dono aqui dentro:
 *
 *  - **divisão por zero.** Às 07h o esperado é 0 e o realizado pode ser positivo — medido,
 *    **8% das interações da equipe acontecem fora de 08:00–17:00**. ★ Sem esperado não há
 *    semáforo: `null`, nunca 0% nem ∞. É a mesma disciplina de `taxa()` em `acumulado.js`.
 *  - **o relógio.** Tudo aqui depende dele, e `agora` entra por PARÂMETRO (regra da WN).
 *  - ★★ **`atualizadoEm`.** A pendência de follow-up é tentadora de resolver por ele, e
 *    medido na rev. 1 do plano: **44%** das tentativas têm `atualizadoEm` em dia diferente.
 *    ⚠ Aqui ela é calculada por **AUSÊNCIA** nas interações da janela. Há guarda de fonte.
 */

import { comparecimentosNoPeriodo } from '@/leads/contagemComparecimento';
import { contaNaMeta, estaPendente } from '@/agenda/pendencia';
import { ETAPAS_DE_FOLLOW_UP, diaDaData, etapaDaInteracao, paraData } from '@/produtividade/dia';
import {
  EXPEDIENTE_PADRAO, expedienteDaPessoa, fracaoDoExpediente, metaAcumulada, metaDoDia,
} from '@/produtividade/expediente';
import { ehFeriado } from '@/produtividade/feriados';
import { creditoNegociador } from '@/metas/credito';
import { temFuncao } from '@/equipe/funcoes';
import { responsavelDaTarefa } from '@/leads/fila';
import { temDesfecho } from '@/leads/oportunidade';

/** Dias em que o lead ainda está vivo no funil — quem some daqui não precisa de contato. */
export const ETAPAS_ABERTAS = ['novo', 'nao_agendou', 'agendado', 'compareceu', 'nao_fechou'];
/** Quantos dias sem toque fazem um lead do negociador virar pendência. */
export const DIAS_SEM_FOLLOW_UP = 3;

/** 'AAAA-MM-DD' + n dias. */
const maisDias = (dia, n) => {
  const [a, m, d] = dia.split('-').map(Number);
  const x = new Date(Date.UTC(a, m - 1, d + n));
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, '0')}-${String(x.getUTCDate()).padStart(2, '0')}`;
};

/**
 * ★★ A SEMANA de um dia: segunda a sábado, como a grade.
 *
 * ⚠ DOMINGO pertence à semana que ACABOU (a segunda anterior até o sábado anterior). A
 * clínica não atende domingo; pôr o domingo na semana seguinte faria o cartão de domingo
 * mostrar uma semana que ainda não começou, com meta cheia e realizado zero.
 */
export function semanaDe(dia) {
  const [a, m, d] = String(dia ?? '').split('-').map(Number);
  if (!a) return [];
  const dow = new Date(Date.UTC(a, m - 1, d)).getUTCDay();   // 0 = domingo
  const recuo = dow === 0 ? 6 : dow - 1;                     // ★ domingo recua 6: pega a semana anterior
  const segunda = maisDias(dia, -recuo);
  return Array.from({ length: 6 }, (_, i) => maisDias(segunda, i));
}

/**
 * ★ O SEMÁFORO do ritmo.
 * @returns 'verde' | 'amarelo' | 'vermelho' — ou `null` quando não há o que cobrar.
 */
export function semaforoDoRitmo(realizado, esperado) {
  // ⚠ Sem esperado NÃO HÁ SEMÁFORO. Zero aqui diria "você falhou" às 07h da manhã.
  if (!Number.isFinite(esperado) || esperado <= 0) return null;
  const razao = Number(realizado || 0) / esperado;
  if (razao >= 1) return 'verde';
  if (razao >= 0.7) return 'amarelo';
  return 'vermelho';
}

/** As interações da pessoa num dia, já filtradas por autor da equipe. */
const porDiaEAutor = (interacoes, autorId, dia) =>
  (interacoes ?? []).filter((i) => i?.autorId === autorId && diaDaData(paraData(i.criadoEm)) === dia);

/**
 * ★★ O CARTÃO DE UMA PESSOA.
 *
 * @param pessoa      `{ id, nome, funcoes }`
 * @param agora       instante de referência — ⚠ obrigatório na prática (regra da WN)
 * @param interacoes  as dos últimos ~4 dias (≈260 docs; o plano mediu)
 * @param agendamentos os da semana
 * @param tarefas     as ABERTAS, que a tela já carrega (`ouvirProximosContatos`)
 * @param agendamentosFuturos ★ PC-2: os de `dataHora ≥ agora` (`buscarAgendamentosFuturos`)
 */
export function cartaoDoDia({
  pessoa, agora = new Date(), hoje = null,
  interacoes = [], agendamentos = [], tarefas = [], leads = [], equipe = [],
  metasDia = {}, expediente = EXPEDIENTE_PADRAO, agendamentosFuturos = [],
  // ★ 02/11/2026 — feriados sem meta (`feriadosDaClinica`); `[]` = o cartão de antes, idêntico
  feriados = [],
} = {}) {
  if (!pessoa?.id) return null;
  /**
   * ★★★ A-2 — O EXPEDIENTE É DA PESSOA, e esta linha é a peça inteira.
   *
   * ⚠⚠ Até 09/out o `expediente` que chegava aqui era **um só para a tela toda** (o da clínica),
   * e era isso que fazia a Talita aparecer *"24 atrás do ritmo"* às 11:00: ★ a conta começava às
   * 08:00, uma hora que ela não trabalha.
   *
   * ★ O parâmetro `expediente` continua existindo e passa a ser a **RESERVA** (o da clínica) —
   * ⚠ quem chama não precisou mudar de assinatura, e quem não tem horário próprio não muda de
   * número.
   */
  const expedienteDela = expedienteDaPessoa(pessoa, expediente);
  const dia = hoje ?? diaDaData(agora);
  const semana = semanaDe(dia);
  const ehNegociador = temFuncao(pessoa, 'negociador') && !temFuncao(pessoa, 'sdr');
  const leadPorId = new Map((leads ?? []).map((l) => [l.id, l]));
  const idsDaEquipe = new Set((equipe ?? []).map((u) => u?.id).filter(Boolean));

  // ---------------------------------------------------------------- ESFORÇO
  const esforcoDoDia = (d) => {
    const doDia = porDiaEAutor(interacoes, pessoa.id, d);
    if (ehNegociador) {
      // ★ follow-up = registro em lead que não fechou (definição D2 do dono).
      return doDia.filter((i) => ETAPAS_DE_FOLLOW_UP.includes(etapaDaInteracao(i, leadPorId.get(i.leadId)).etapa)).length;
    }
    // ★ SDR: LEADS DISTINTOS tentados — clicar duas vezes no mesmo lead não muda o número.
    return new Set(doDia.filter((i) => i.tipo === 'ligacao' && i.leadId).map((i) => i.leadId)).size;
  };

  const chaveMeta = ehNegociador ? 'followUpsDia' : 'ligacoesDia';
  const metaDiaria = Number(metasDia?.[pessoa.id]?.[chaveMeta]) || 0;
  const metaHoje = metaDoDia(metaDiaria, dia, expedienteDela, feriados);
  const feriadoHoje = ehFeriado(dia, feriados);
  // ⚠ Só os dias da semana ATÉ hoje: cobrar a semana inteira na segunda seria absurdo.
  const semanaAteHoje = semana.filter((d) => d <= dia);

  const esforco = {
    hoje: esforcoDoDia(dia),
    meta: metaHoje,
    semana: semanaAteHoje.reduce((t, d) => t + esforcoDoDia(d), 0),
    metaSemana: metaAcumulada(metaDiaria, semanaAteHoje, expedienteDela, feriados),
    unidade: ehNegociador ? 'follow-ups' : 'ligações',
  };

  // ---------------------------------------------------------------- RITMO
  /**
   * ⚠ `fracaoDoExpediente` devolve `null` no domingo — e `null` NÃO é zero. Zero é "o dia
   * ainda não começou"; `null` é "não há ritmo a cobrar". Os dois descem diferentes.
   */
  // ★★ Feriado: NÃO HÁ RITMO a cobrar — `null`, como no domingo (o semáforo some junto).
  const fracao = feriadoHoje ? null : fracaoDoExpediente(agora, dia, expedienteDela);
  const esperado = fracao === null ? null : Math.round(metaHoje * fracao);
  const ritmo = {
    fracao,
    esperado,
    semaforo: esperado === null ? null : semaforoDoRitmo(esforco.hoje, esperado),
    atraso: esperado === null ? null : Math.max(0, esperado - esforco.hoje),
  };

  // ---------------------------------------------------------------- RESULTADO
  const resultado = ehNegociador
    ? resultadoDoNegociador({ pessoa, dia, semanaAteHoje, leads, agendamentos, agora })
    : resultadoDoSdr({ pessoa, dia, semanaAteHoje, agendamentos, agora });

  // ---------------------------------------------------------------- PENDÊNCIAS
  const pendencias = pendenciasDaPessoa({
    pessoa, leads, tarefas, agendamentos, interacoes, idsDaEquipe, agora, ehNegociador, agendamentosFuturos,
  });

  return {
    id: pessoa.id,
    nome: pessoa.nome ?? '',
    papel: ehNegociador ? 'negociador' : 'SDR',
    dia,
    /**
     * ★★ O expediente DELA viaja no cartão — ⚠ não é dado a mais por acaso.
     *
     * ★★★ `PlacarDoDia` recebia **um** expediente para a lista inteira e desenhava o rótulo com
     * ele. ⚠ Com horário por pessoa, o rótulo da Talita e o da Jéssica são diferentes, e quem
     * sabe qual é o de quem é **este cálculo**, não o componente.
     */
    expediente: expedienteDela,
    semMeta: metaDiaria <= 0,
    // ⚠ a chave só existe no feriado: fora dele, o cartão é o de antes, byte a byte
    ...(feriadoHoje ? { feriadoHoje: true } : {}),
    esforco,
    ritmo,
    resultado,
    pendencias,
  };
}

/**
 * ★★ O resultado da SDR: agendamentos que CONTAM na meta (D2), por QUEM MARCOU.
 *
 * ⚠ `agendadoPorId`, não `creditoSdr` — é a régua da grade, e no lote 3b passou a ser
 * também a de Metas e do Placar. ★ A pergunta é "o que ELA fez hoje", não "de quem é o lead".
 */
function resultadoDoSdr({ pessoa, dia, semanaAteHoje, agendamentos, agora }) {
  const dias = new Set(semanaAteHoje);
  const dela = (a) => a?.agendadoPorId === pessoa.id && contaNaMeta(a, agora);
  const quando = (a) => diaDaData(paraData(a.criadoEm));
  return {
    tipo: 'sdr',
    hoje: (agendamentos ?? []).filter((a) => dela(a) && quando(a) === dia).length,
    semana: (agendamentos ?? []).filter((a) => dela(a) && dias.has(quando(a))).length,
    rotulo: 'agendamentos',
  };
}

/**
 * ★★ O resultado do NEGOCIADOR: fechamentos e a conversão da semana.
 *
 * ⚠ O denominador é "comparecimentos dos leads em que ELE é o negociador" — não os
 * creditados a ele. Medido (plano §7): o JOABE fechou **9** e tinha **0** comparecimentos
 * creditados; `9 ÷ 0` é indefinido. ★ Com o denominador certo dá 39,1%.
 * ⚠ E `null` quando o denominador é zero — nunca 0% nem ∞.
 */
function resultadoDoNegociador({ pessoa, dia, semanaAteHoje, leads, agendamentos, agora }) {
  const dias = new Set(semanaAteHoje);
  const dele = (l) => creditoNegociador(l).id === pessoa.id;
  const fechouEm = (l) => diaDaData(paraData(l.dataFechamento));
  const fechados = (leads ?? []).filter((l) => l?.etapa === 'fechado' && dele(l));
  const naSemana = fechados.filter((l) => dias.has(fechouEm(l))).length;
  const compareceramNaSemana = comparecimentosNoPeriodo(
    (leads ?? []).filter((l) => l?.negociadorId === pessoa.id),
    agendamentos,
    (q) => dias.has(diaDaData(q)),
  ).length;
  return {
    tipo: 'negociador',
    hoje: fechados.filter((l) => fechouEm(l) === dia).length,
    semana: naSemana,
    rotulo: 'fechamentos',
    compareceramNaSemana,
    // ⚠ null em denominador zero — a mesma regra de `taxa()` em `acumulado.js`.
    conversaoSemana: compareceramNaSemana > 0 ? (naSemana / compareceramNaSemana) * 100 : null,
    agora,
  };
}

/** ★ PC-2 — leads com agendamento `status: 'agendado'` e `dataHora ≥ agora`. PURO. */
export function leadsComConsultaFutura(agendamentos, agora = new Date()) {
  return new Set(
    (agendamentos ?? [])
      .filter((a) => a?.leadId && a.status === 'agendado' && paraData(a.dataHora) && paraData(a.dataHora) >= agora)
      .map((a) => a.leadId),
  );
}

/** As três pendências, cada uma com o número e o destino do link. */
function pendenciasDaPessoa({
  pessoa, leads, tarefas, agendamentos, interacoes, idsDaEquipe, agora, ehNegociador, agendamentosFuturos,
}) {
  /**
   * ★ "Leads sem próximo contato" — custo ZERO: as tarefas abertas já estão na tela
   * (`ouvirProximosContatos`), e o resto é conta em memória.
   * ⚠ Usa `responsavelDaTarefa`, que é a MESMA régua do filtro da tela (dono gravado OU
   * herdado do SDR do lead) — inventar outra aqui faria o link abrir uma lista diferente
   * do número que o cartão mostrou.
   */
  const comTarefa = new Set(
    (tarefas ?? [])
      .filter((t) => t?.leadId && responsavelDaTarefa(t, leads).id === pessoa.id)
      .map((t) => t.leadId),
  );
  /**
   * ★★★ "SEM PRÓXIMO CONTATO" = LEAD **TOCADO** NA JANELA, SEM TAREFA ABERTA.
   *
   * ⚠⚠ A primeira versão era "todo lead aberto dela sem tarefa", e o número medido em
   * produção foi **6.956** para a Jéssica. ★ A conta estava certa; o indicador é que não
   * era um chamado à ação: a clínica tem **~7.000 leads vivos e 220 tarefas abertas**, logo
   * aquilo media o **acervo nunca trabalhado**, não o que falta fazer hoje. ⚠ Um cartão com
   * 6.956 é um número que a pessoa aprende a ignorar no primeiro dia.
   *
   * ★★ Decisão do dono (3/out): conta só quem foi **TOCADO nos últimos 4 dias** — a janela
   * que o cartão já carrega, sem consulta nova. A pergunta vira a certa: *"você trabalhou
   * este lead e não deixou o próximo contato marcado"*.
   *
   * ⚠ "Tocado" é QUALQUER interação na janela, de qualquer autor — inclusive a mensagem que
   * o PACIENTE mandou. ★ De propósito: paciente que escreveu e ficou sem próximo passo é
   * exatamente o caso que não pode escapar.
   *
   * ★ `temDesfecho` continua filtrando: é a regra da casa (VG) para "a história acabou".
   */
  /**
   * ⚠ NOME PRÓPRIO, e não é zelo: há OUTRO conjunto de "tocados" logo abaixo, o da pendência
   * de follow-up — e ele usa uma janela DIFERENTE (3 dias, não 4). Dois `tocadosNaJanela` no
   * mesmo arquivo seriam dois significados com um nome só; o `no-shadow` pegou, e o conserto
   * é dizer qual é qual.
   */
  const tocadosNos4Dias = new Set(
    (interacoes ?? []).map((i) => i?.leadId).filter(Boolean),
  );
  const abertosDela = (leads ?? []).filter(
    (l) => ETAPAS_ABERTAS.includes(l?.etapa) && !temDesfecho(l)
      && tocadosNos4Dias.has(l.id)
      && (l.sdrId === pessoa.id || l.sdrOriginalId === pessoa.id),
  );
  /**
   * ★★ PC-2 (decisão 1 do dono) — lead em `agendado` COM consulta futura ativa já tem o
   * próximo passo marcado: a consulta. Com a PC-2 encerrando as tarefas ao agendar, sem esta
   * linha o cartão passaria a cobrar justamente quem acabou de agendar (medido: 41 → 53).
   * ⚠ Consulta que passou sem baixa (`dataHora < agora`) ou cancelada NÃO conta — o lead
   * volta a ser cobrado, como o dono decidiu.
   */
  const comConsultaFutura = leadsComConsultaFutura(agendamentosFuturos, agora);
  const semProximoContato = abertosDela.filter(
    (l) => !comTarefa.has(l.id) && !(l.etapa === 'agendado' && comConsultaFutura.has(l.id)),
  ).length;

  /**
   * ★★ "Agendamentos sem baixa" — SÓ os que a EQUIPE agendou.
   * ⚠ Medido: são 757 no total, mas **698 da "Importação planilha 2026"**. O número cru
   * assustaria sem significar trabalho de ninguém.
   */
  const semBaixa = (agendamentos ?? []).filter(
    (a) => a?.agendadoPorId === pessoa.id && idsDaEquipe.has(a.agendadoPorId) && estaPendente(a, agora),
  ).length;

  const pend = {
    semProximoContato: { n: semProximoContato, para: '/proximos-contatos' },
    semBaixa: { n: semBaixa, para: '/agenda?pendentes=1' },
  };

  if (ehNegociador) {
    /**
     * ★★★ "Leads que não fecharam sem follow-up há 3+ dias" — por AUSÊNCIA.
     *
     * ⚠⚠ O lead NÃO guarda a data da última interação: medido, `ultimaTentativaEm` existe em
     * **4 dos 350** leads em `nao_fechou` (1%). ★ Então a conta é: dos leads dele em
     * `nao_fechou`/`compareceu`, quais NÃO aparecem nas interações da janela de 3 dias.
     *
     * ⚠ Lead criado há menos de 3 dias NÃO entra — ele ainda não teve tempo de ficar sem
     * follow-up, e `criadoEm` existe em 100% dos leads.
     */
    const desde = new Date(agora.getTime() - DIAS_SEM_FOLLOW_UP * 864e5);
    const tocadosNos3Dias = new Set(
      (interacoes ?? [])
        .filter((i) => i?.leadId && paraData(i.criadoEm) && paraData(i.criadoEm) >= desde)
        .map((i) => i.leadId),
    );
    const semFollowUp = (leads ?? []).filter((l) => {
      if (!ETAPAS_DE_FOLLOW_UP.includes(l?.etapa)) return false;
      if (creditoNegociador(l).id !== pessoa.id) return false;
      const nasceu = paraData(l.criadoEm);
      if (nasceu && nasceu >= desde) return false;   // ⚠ novo demais para cobrar
      return !tocadosNos3Dias.has(l.id);
    }).length;
    pend.semFollowUp = { n: semFollowUp, dias: DIAS_SEM_FOLLOW_UP, para: '/proximos-contatos' };
  }

  return pend;
}

/** Os cartões que QUEM OLHA pode ver: gestor vê todos; os demais, só o próprio. */
export function placarDoDia({ equipe = [], uid = null, vejoTudo = false, ...resto } = {}) {
  const pessoas = (equipe ?? []).filter(
    (u) => u?.ativo !== false && (temFuncao(u, 'sdr') || temFuncao(u, 'negociador')),
  );
  const visiveis = vejoTudo ? pessoas : pessoas.filter((u) => u.id === uid);
  return visiveis.map((pessoa) => cartaoDoDia({ pessoa, equipe, ...resto })).filter(Boolean);
}
